import React, { useCallback, useEffect, useRef } from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useUserSettingsContext } from '../../context/UserSettingsContext';
import { useTheme } from '../../context/ThemeContext';
import { getUserSettings } from '../../utils/api';
import { applyUiPreferences, shellFromPreferences } from '../../utils/uiPreferences';
import ClassicLayout from './ClassicLayout';
import InsetLayout from './InsetLayout';
import MobileLayout from './MobileLayout';
import MobileInsetLayout from './mobile/MobileInsetLayout';

/**
 * Shell switch (desktop and phone; `isMobile` is App.js's ≤768px cut). The layout is a per-user preference
 * (Settings → Appearance → Layout) stored on the Cosmos user doc as
 * `uiPreferences.shell` so it follows the user across devices, and mirrored
 * in localStorage (`uiShell`) so the pre-paint script and first render agree
 * before the doc arrives. Server value wins whenever it is present.
 *
 * Users who never chose a layout (no `uiPreferences.shell`) get the org
 * default: PostHog flag `new-look-default` (org-gated) → New, else Classic.
 * The default is applied locally only, never written to the user doc, so an
 * explicit choice in Settings still wins and turning the flag off reverts.
 */
const DEFAULT_SHELL_FLAG = 'new-look-default';

const MainLayout = ({ isMobile = false }) => {
  const { userSettings } = useUserSettingsContext();
  const theme = useTheme();
  const { uiShell } = theme;
  const themeRef = useRef(theme);
  themeRef.current = theme;
  const serverPrefs = userSettings?.uiPreferences;
  const hasServerPrefs = userSettings ? 'uiPreferences' in userSettings : false;
  const email = userSettings?.email;
  // undefined while flags load; the local copy holds until it resolves.
  const newLookDefault = useFeatureFlagEnabled(DEFAULT_SHELL_FLAG);
  const orgDefaultShell = userSettings && newLookDefault !== undefined
    ? (newLookDefault ? 'inset' : 'classic')
    : null;

  // Apply whatever the user doc carries that differs from the local copy:
  // layout, chart palette, sidebar preset, custom colours (utils/uiPreferences).
  const applyServerPrefs = useCallback((prefs) => applyUiPreferences(themeRef.current, prefs), []);

  useEffect(() => {
    applyServerPrefs(serverPrefs);
  }, [serverPrefs, applyServerPrefs]);

  // No saved choice: follow the org default (only once the doc is known to
  // carry uiPreferences, so an old API host's missing projection can't mask
  // a real choice before the fallback fetch below lands).
  useEffect(() => {
    if (!hasServerPrefs || shellFromPreferences(serverPrefs) || !orgDefaultShell) return;
    if (themeRef.current.uiShell !== orgDefaultShell) themeRef.current.setUiShell(orgDefaultShell);
  }, [hasServerPrefs, serverPrefs, orgDefaultShell]);

  // initData projects `uiPreferences`; an API host built before that field
  // existed omits it, so fall back to the full user doc once per load.
  useEffect(() => {
    if (!email || hasServerPrefs) return undefined;
    let cancelled = false;
    getUserSettings(email)
      .then((doc) => { if (!cancelled) applyServerPrefs(doc?.uiPreferences); })
      .catch(() => { /* keep the local choice */ });
    return () => { cancelled = true; };
  }, [email, hasServerPrefs, applyServerPrefs]);

  // Render from the server value the moment it is known (the effect above
  // syncs ThemeContext right after), so the other layout never paints first.
  const shell = shellFromPreferences(serverPrefs)
    || (hasServerPrefs && orgDefaultShell)
    || uiShell;
  if (isMobile) return shell === 'inset' ? <MobileInsetLayout /> : <MobileLayout />;
  return shell === 'inset' ? <InsetLayout /> : <ClassicLayout />;
};

export default MainLayout;
